import {
  AnalyzeCsvRequest,
  AnalyzeCsvResponse,
  CreateAudience,
  ImportAudienceContext,
  ImportRequest,
  ImportResponse,
  LeadImport,
  PatchAudience,
  ReadAudience,
  ReadLead,
  UpdateAudience,
} from '@tinct/core-api-models';
import {ApiClient} from '../api-client/api-client';
import {ApiCollectionResponse, GetCollectionQueryParams, GetQueryByIdParams,} from '../types';

export type ValidateAudienceData = Partial<CreateAudience> | Partial<UpdateAudience> | PatchAudience;

export interface GetAudiencesFilter {
  organizationId?: string;
}

export interface GetAudienceLeadsStaticFilter {
  audienceId: string;
}

export class AudiencesEndpoint {
  constructor(private client: ApiClient) {}

  async getAudience(params: GetQueryByIdParams<ReadAudience>): Promise<ReadAudience> {
    return this.client.getById<ReadAudience>(
      `/api/v1/audiences/${params.id}`,
      params.fields,
    );
  }

  async getAudiences(
    params: GetCollectionQueryParams<ReadAudience, GetAudiencesFilter>,
  ): Promise<ApiCollectionResponse<ReadAudience>> {
    const { fields, offset, limit, filters, staticParams, sort } = params;
    return this.client.getCollection<ReadAudience>('/api/v1/audiences', fields, {
      offset,
      limit,
      ...filters,
      ...staticParams,
      sort,
    });
  }

  async createAudience(data: CreateAudience): Promise<ReadAudience> {
    return this.client.post<ReadAudience>('/api/v1/audiences', data);
  }

  async validateAudience(data: ValidateAudienceData, group: string): Promise<void> {
    return this.client.post<void>('/api/v1/audiences/_validate', data, {
      params: { group },
    });
  }

  async updateAudience(id: string, data: UpdateAudience): Promise<ReadAudience> {
    return this.client.put<ReadAudience>(`/api/v1/audiences/${id}`, data);
  }

  async patchAudience(id: string, data: PatchAudience): Promise<ReadAudience> {
    return this.client.patch<ReadAudience>(`/api/v1/audiences/${id}`, data);
  }

  async deleteAudience(id: string): Promise<void> {
    return this.client.delete(`/api/v1/audiences/${id}`);
  }

  async analyzeCsv(
    data: AnalyzeCsvRequest<ImportAudienceContext>
  ): Promise<AnalyzeCsvResponse> {
    return this.client.post<AnalyzeCsvResponse>('/api/v1/audiences/_analyze-csv', data);
  }

  async importLeads(
    data: ImportRequest<LeadImport, ImportAudienceContext>
  ): Promise<ImportResponse> {
    return this.client.post<ImportResponse>('/api/v1/audiences/_import', data);
  }

  async removeLeadFromAudience(audienceId: string, leadId: string): Promise<void> {
    return this.client.delete(`/api/v1/audiences/${audienceId}/leads/${leadId}`);
  }

  async getAudienceLeads(
    params: GetCollectionQueryParams<ReadLead, GetAudienceLeadsStaticFilter>,
  ): Promise<ApiCollectionResponse<ReadLead>> {
    const { staticParams, fields, sort, offset = 0, limit = 20, filters } = params;

    return this.client.getCollection<ReadLead>(
      `/api/v1/audiences/${staticParams?.audienceId}/leads`,
      fields,
      {
        offset,
        limit,
        ...filters,
        sort
      },
    );
  }
}
